import SockJS from 'sockjs-client'
import {Stomp} from '@stomp/stompjs'
import * as auth from '../../../modules/auth/redux/AuthRedux'
import * as device from '../../../redux/DevicesRedux'
import {
  canvasMap,
  clearVideoRecording,
  ctxMap,
  mediaRecorderMap,
  setCommandStatus,
  setLiveStream,
  setRecordedVideo,
  setSettingFile,
  setSnapShots,
  startRecording,
  stopRecording,
  toggleLiveStream,
} from '../../../redux/WebSocketSlice'
import {decodeSnapshot, decodeStream} from '../../../../utills/StreamDecoder'
import {setResponse} from '../../../redux/ResponseModalSlice'
import store from '../../../../setup/redux/Store'
import {downloadZip} from '../../../../utills/downloadZip'
import _ from 'lodash'
import {saveSnapshot, saveVideo} from '../../../../utills/saveFile'
import { timeCounter } from '../../../../utills/timeCounter'

interface WebSocketProps {
  customerId: any
  accessToken: any
  SOCKET_URL: string
  intl: any
  // onMessageReceived: (message: any) => void;
  // onConnect: () => void;
  // onDisconnect: () => void;
  dispatch: any
}

export const initializeWebSocket = ({
  customerId,
  accessToken,
  SOCKET_URL,
  intl,
  // onMessageReceived,
  // onConnect,
  // onDisconnect,
  dispatch,
}: WebSocketProps) => {
  const RECONNECT_INTERVAL = 5000
  let clientRef: any = null
  let reconnectTimeoutRef: any = null
  let liveStreamStartTime: number | null = null // First event timestamp
  let lastLiveStreamTime: number | null = null // Last event timestamp
  let lastEventChecker: any = null // Interval to check last event
  let isCheckingLastEvent = false // Prevent multiple intervals
  let isDisconnecting: any = false // Flag to prevent reconnection during disconnect

  localStorage.removeItem('ssid')
  dispatch(auth.actions.subscribe({}))

  const headers = {
    'access-token': accessToken,
  }

  const connect = () => {
    if (isDisconnecting) {
      console.log('Skipping connection - disconnect in progress')
      return
    }

    const socket = new SockJS(SOCKET_URL, null, {transports: ['websocket']})
    const client = Stomp.over(socket)
    clientRef = client

    client.connect(headers, onConnected, onError)

    client.onWebSocketClose = (event: CloseEvent) => {
      console.warn('WebSocket connection closed:', event)
      if (!isDisconnecting) {
        reconnect()
      }
    }
  }

  const onConnected = (frame: any) => {
    console.log('Connected: ' + frame)
    const subscriptionChannel = `/user/${customerId}/event`
    clientRef.subscribe(subscriptionChannel, (message: any) => onMessageReceived(message), headers)
    // onConnect();
  }

  const onError = (error: any) => {
    console.error('Connection error:', error)
    if (!isDisconnecting) {
      reconnect()
    }
  }

  const reconnect = () => {
    if (isDisconnecting) {
      console.log('Skipping reconnection - disconnect in progress')
      return
    }

    if (!accessToken) {
      console.warn('Access token not present. Skipping reconnection.')
      disconnect()
      return
    }

    if (reconnectTimeoutRef) {
      clearTimeout(reconnectTimeoutRef)
    }
    reconnectTimeoutRef = setTimeout(() => {
      console.log('Reconnecting...')
      connect()
    }, RECONNECT_INTERVAL)
  }

  const disconnect = () => {
    console.log('Disconnecting WebSocket...')
    
    isDisconnecting = true
    
    if (reconnectTimeoutRef) {
      clearTimeout(reconnectTimeoutRef)
      reconnectTimeoutRef = null
    }
    
    if (lastEventChecker) {
      clearInterval(lastEventChecker)
      lastEventChecker = null
    }
    
    liveStreamStartTime = null
    lastLiveStreamTime = null
    isCheckingLastEvent = false
    
    if (clientRef) {
      try {
        clientRef.disconnect(() => {
          console.log('WebSocket disconnected successfully')
          clientRef = null
        })
      } catch (error) {
        console.error('Error during disconnect:', error)
        clientRef = null
      }
    }
    
    localStorage.removeItem('ssid')
  }

  if (!accessToken) {
    console.error('Access token not present. Cannot establish WebSocket connection.')
    return {disconnect: () => {}}
  }

  connect()

  const onMessageReceived = (message: any) => {
    if (message.body) {
      const parsedBody = JSON.parse(message.body)
      handleSocketEvent(parsedBody)
    }
  }

  const handleSocketEvent = (eventData: any) => {
    switch (eventData.type) {
      case 'subscribe':
        console.log('Subscribed:', eventData)
        localStorage.setItem('ssid', btoa(eventData.data))
        dispatch(auth.actions.subscribe(eventData))
        // localStorage.setItem('socketSessionId', eventData.data);
        break
      case 'siteInfo':
        console.log('Site Info:', eventData)
        break
      case 'deviceList':
        console.log('Device List:', eventData)
        dispatch(device.actions.setAllDevices(eventData.data))
        break
      case 'analyzeInfo':
        console.log('Analyze Info:', eventData)
        break
      case 'liveList':
        console.log('Live List:', eventData)
        dispatch(device.actions.setLiveDevices(eventData.data))
        break
      case 'dailyList':
        console.log('Daily List:', eventData)
        if (eventData.data && eventData.data.length > 0) {
          const eventsMap = eventData.data.reduce((acc: any, device: any) => {
            acc[device.DeviceId] = device
            return acc
          }, {})
          dispatch(device.actions.setDailyList(eventsMap))
        }
        break
      case 'camChk':
        console.log('Camera Check:', eventData)
        if (eventData.data) {
          const fileName = eventData.data?.FileName
          if (fileName) {
            const fullUrl = `https://cdn.vehiclets.com:440/${fileName}`
            downloadZip(fullUrl)
          } else {
            console.log('No file available to download.')
          }
        }
        break
      case 'liveInfo':
        console.log('Live Info:', eventData)
        dispatch(device.actions.setDeviceLiveInfo(eventData.data))
        break
      case 'monthlyList':
        console.log('Monthly List:', eventData)
        break
      case 'trackingData':
        console.log('Tracking Data:', eventData)
        dispatch(device.actions.setDeviceDrivingInfo(eventData.data))
        break
      case 'eventData':
        console.log('Event Data:', eventData)
        break
      case 'dailyDeviceReport':
        console.log('Daily Device Report:', eventData)
        break
      case 'monthlyDeviceReport':
        console.log('Monthly Device Report:', eventData)
        break
      case 'commandSuccessful':
        console.log('Command Successful:', eventData)
        break
      case 'getSettingFileResponse':
        console.log('Get Setting File Response:', eventData)
        dispatch(setSettingFile({settingFile: eventData.data}))
        break
      case 'recordTimeTableResponse':
        console.log('Record Time Table Response:', eventData)
        break
      case 'recordDateResponse':
        console.log('Record Date Response:', eventData)
        break
      case 'videoFrameResponse':
        console.log('Video Frame Response:', eventData)
        break
      case 'systemLog':
        console.log('System Log:', eventData)
        break
      case 'liveStream':
        console.log('Live Stream:', eventData)
        if (!store.getState().webSocket.isLiveStreamActive) {
          return
        }

        const currentSystemTime = Date.now() // Get current time in milliseconds

        // Store the timestamp of the first received live stream event
        if (liveStreamStartTime === null) {
          liveStreamStartTime = currentSystemTime
          // console.log("First live stream event at:", new Date(liveStreamStartTime).toISOString());
        }

        // Update the timestamp of the last received live stream event
        lastLiveStreamTime = currentSystemTime
        // console.log("Last received live stream event at:", new Date(lastLiveStreamTime).toISOString());

        const elapsedTime = (currentSystemTime - liveStreamStartTime) / 1000 // Convert to seconds

        // If 60 seconds have passed, stop the live stream
        if (elapsedTime >= 60) {
          // console.log("Live stream exceeded 60 seconds. Stopping...");
          dispatch(toggleLiveStream(false))
          dispatch(stopRecording())
          liveStreamStartTime = null
          lastLiveStreamTime = null
          return
        }

        const decStreams = decodeStream(eventData.data.Body.Images)
        // const stream: any = `data:image/jpeg;base64,${decStream}`;
        const streams: any = decStreams.map((stream: string) => `data:image/png;base64,${stream}`)
        const timestamp = new Date().toISOString()
        dispatch(setLiveStream({liveStream: streams, timestamp: timestamp}))
        const cameraIds = streams.map((_: any, index: any) => `camera${index + 1}`)

        if (
          !mediaRecorderMap.has(cameraIds[0]) ||
          mediaRecorderMap.get(cameraIds[0])!.state === 'inactive'
        ) {
          dispatch(clearVideoRecording())
          dispatch(startRecording(cameraIds))
        }

        streams.forEach((stream: any, index: any) => {
          const cameraId = cameraIds[index]
          const canvas = canvasMap.get(cameraId)
          const ctx = ctxMap.get(cameraId)

          if (!canvas) return

          const img = new Image()
          img.src = stream

          img.onload = () => {
            canvas.width = img.width
            canvas.height = img.height
            ctx?.drawImage(img, 0, 0, canvas.width, canvas.height)
          }
        })

        if (!isCheckingLastEvent) {
          isCheckingLastEvent = true
          lastEventChecker = setInterval(() => {
            const now = Date.now()
            if (lastLiveStreamTime && now - lastLiveStreamTime >= 5000) {
              // console.log("No new live stream event received in 5 seconds. Stopping stream...");
              dispatch(toggleLiveStream(false))
              dispatch(stopRecording())
              liveStreamStartTime = null
              lastLiveStreamTime = null
              clearInterval(lastEventChecker)
              isCheckingLastEvent = false
            }
          }, 1000)
        }

        break
      case 'snapShot':
        console.log('Snap Shot:', eventData)
        // const decImage = decodeSnapshot(eventData.data.Body.Bin);
        // const imageSrc = `data:image/png;base64,${decImage}`;
        // dispatch(setSnapShot(imageSrc));
        timeCounter.stop();
        const sanpshotResponseTime=timeCounter.getTime();
        const formatSanpshotResponseTime=timeCounter.formatTime(sanpshotResponseTime)        
        const decImages = decodeSnapshot(eventData.data.Body.Bin)
        const imageSrcs = decImages.map((image: string) => `data:image/jpeg;base64,${image}`)
        const imageTimestamp = new Date().toISOString()
        dispatch(setSnapShots({snapShots: imageSrcs, timestamp: imageTimestamp}))
        const selectedDevice = store.getState().device.selectedDevice
        const decodedBinary = atob(eventData.data.Body.Bin)
        const fileSizeInBytes = decodedBinary.length
        if (selectedDevice && !_.isEmpty(selectedDevice) && imageSrcs && imageSrcs.length > 0 && formatSanpshotResponseTime) {
          saveSnapshot(selectedDevice, imageSrcs, intl, fileSizeInBytes, formatSanpshotResponseTime)
        }
        break
      case 'recordVideoResponse':
        console.log('Recorded Video Response:', eventData)
          timeCounter.stop();
          const videoResponsetime=timeCounter.getTime();
          const formatVideoResponseTime=timeCounter.formatTime(videoResponsetime)
        dispatch(setRecordedVideo({recordedVideo: eventData.data}))
        const selectedDeviceInfo = store.getState().device.selectedDevice
        if (selectedDeviceInfo && !_.isEmpty(selectedDeviceInfo) && eventData && formatVideoResponseTime) {
          saveVideo(eventData.data, intl, dispatch, selectedDeviceInfo, formatVideoResponseTime)
        }
        break
      case 'commandStatus':
        console.log('commandStatus', eventData)
        dispatch(setCommandStatus(eventData.data))
         
        if (eventData.data.ResCode == 3) {
           timeCounter.stop();
           timeCounter.reset();
        }

        const {ResStr} = eventData.data

        switch (ResStr) {
          case 'DMSCMD_DEVICEREBOOT':
            dispatch(
              setResponse({
                response: eventData.data,
              })
            )
            break
          default:
            console.log('Unknown command status:', ResStr)
            break
        }
        break
      default:
        console.log('Unknown event type:', eventData)
    }
  }

  return {
    disconnect,
  }
}
